import { useEffect, useState } from "react";
import { X, Download, Share2 } from "lucide-react";

const LS_DISMISSED = "fromhere_pwa_dismissed_v1";

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)")?.matches ||
    window.navigator.standalone === true
  );
}

function isIos() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
}

function isAndroid() {
  return typeof navigator !== "undefined" && /Android/i.test(navigator.userAgent || "");
}

function isAndroidNonChrome() {
  const ua = (typeof navigator !== "undefined" && navigator.userAgent) || "";
  return isAndroid() && (!/Chrome\//.test(ua) || /SamsungBrowser|EdgA|OPR|Firefox|FxiOS|YaBrowser|UCBrowser|MiuiBrowser|Brave/i.test(ua));
}

export default function InstallPrompt() {
  const [deferredEvt, setDeferredEvt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showIosHelp, setShowIosHelp] = useState(false);
  const [showChromeTip, setShowChromeTip] = useState(false);

  // Wait for the cookie banner to be acknowledged before showing this — otherwise
  // two floating cards fight for the bottom of the screen on first visit.
  const cookieAcknowledged = () => {
    try { return localStorage.getItem("fromhere_cookie_ack_v1") === "1"; }
    catch { return true; }
  };

  useEffect(() => {
    if (isStandalone()) return;
    if (localStorage.getItem(LS_DISMISSED) === "1") return;

    // Chromium / Edge / Android
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredEvt(e);
      // Delay if the cookie banner hasn't been dismissed yet — poll every 2s.
      const showWhenReady = () => {
        if (cookieAcknowledged()) setVisible(true);
        else setTimeout(showWhenReady, 2000);
      };
      showWhenReady();
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    // Non-Chrome Android browsers can package the app in a way Play Protect blocks.
    if (isAndroidNonChrome()) {
      const t = setTimeout(() => {
        if (!isStandalone() && localStorage.getItem(LS_DISMISSED) !== "1" && cookieAcknowledged()) {
          setVisible(true);
          setShowChromeTip(true);
        }
      }, 5000);
      return () => {
        clearTimeout(t);
        window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      };
    }

    // iOS Safari — no beforeinstallprompt, show a helper card after a delay
    if (isIos()) {
      const t = setTimeout(() => {
        if (!isStandalone() && localStorage.getItem(LS_DISMISSED) !== "1" && cookieAcknowledged()) {
          setVisible(true);
          setShowIosHelp(true);
        }
      }, 5000);
      return () => {
        clearTimeout(t);
        window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      };
    }
    return () => window.removeEventListener("beforeinstallprompt", onBeforeInstall);
  }, []);

  const dismiss = () => {
    localStorage.setItem(LS_DISMISSED, "1");
    setVisible(false);
  };

  const install = async () => {
    if (!deferredEvt) return;
    deferredEvt.prompt();
    try {
      await deferredEvt.userChoice;
    } catch {}
    setDeferredEvt(null);
    setVisible(false);
    localStorage.setItem(LS_DISMISSED, "1");
  };

  if (!visible) return null;

  return (
    <div
      data-testid="pwa-install-prompt"
      className="fixed left-3 right-3 sm:left-auto sm:right-4 sm:w-[360px] bottom-3 safe-bottom-tight z-[10000] rounded-2xl shadow-2xl border border-black/10 bg-[#f7f2e7] text-[#2a3a2f] p-4 backdrop-blur"
      style={{ fontFamily: "'Manrope', sans-serif" }}
    >
      <button
        data-testid="pwa-install-dismiss"
        onClick={dismiss}
        aria-label="Dismiss install prompt"
        className="absolute top-2 right-2 p-1 rounded-full hover:bg-black/5"
      >
        <X className="w-4 h-4" />
      </button>
      <div className="flex items-start gap-3">
        <img
          src="/brand/from-here-pin-128.png"
          alt="From Here"
          className="w-11 h-11 rounded-xl shrink-0"
        />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold">Install From Here</div>
          {showChromeTip ? (
            <p data-testid="pwa-install-chrome-tip" className="text-xs opacity-80 mt-1 leading-relaxed">
              For the best experience, open this page in{" "}
              <span className="font-medium">Chrome</span> and tap <span className="font-medium">Install</span> there.
              Other browsers can trigger an "unsafe app" warning on Android.
            </p>
          ) : showIosHelp ? (
            <p className="text-xs opacity-80 mt-1 leading-relaxed">
              Tap <Share2 className="inline w-3.5 h-3.5 mx-0.5 -mt-0.5" />{" "}
              <span className="font-medium">Share</span> then{" "}
              <span className="font-medium">Add to Home Screen</span> to install.
            </p>
          ) : (
            <p className="text-xs opacity-80 mt-1 leading-relaxed">
              Add it to your home screen for a full-screen, app-like experience.
              {isAndroid() && <span data-testid="pwa-install-android-tip" className="block mt-1">Tip: install from Chrome for the best experience.</span>}
            </p>
          )}
        </div>
      </div>
      {!showIosHelp && !showChromeTip && deferredEvt && (
        <button
          data-testid="pwa-install-btn"
          onClick={install}
          className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#2a3a2f] text-[#f7f2e7] py-2 text-sm font-medium hover:opacity-90 transition"
        >
          <Download className="w-4 h-4" />
          Install
        </button>
      )}
    </div>
  );
}
